import { useQuery } from '@tanstack/react-query'
import { Wallet } from 'lucide-react'
import { positionsApi } from '../api/client'
import { heatScale } from '../lib/heatScale'
import './HoldingsPanel.css'

interface HoldingsPanelProps {
  accountId: number
}

type Position = Awaited<ReturnType<typeof positionsApi.getAll>>[number]

function fmtUSD(v: number): string {
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtSignedUSD(v: number): string {
  if (v === 0) return '$0.00'
  return `${v > 0 ? '+' : '−'}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtSignedPct(v: number): string {
  const sign = v >= 0 ? '+' : ''
  return `${sign}${v.toFixed(2)}%`
}

/**
 * Live per-account broker holdings — the actual GET /api/positions?account_id=
 * snapshot, refreshed every 30s. Distinct from the once-daily conviction
 * lifecycle count elsewhere on the page: this is what the book is actually
 * holding right now, not a lagging pick-pipeline tally.
 */
export function HoldingsPanel({ accountId }: HoldingsPanelProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['positions', accountId],
    queryFn: () => positionsApi.getAll(accountId),
    retry: 1,
    refetchInterval: 30000,
  })

  const rows: Position[] = data ?? []
  const ordered = [...rows].sort((a, b) => b.market_value - a.market_value)
  const totalValue = rows.reduce((sum, p) => sum + p.market_value, 0)
  const hasData = data !== undefined
  // Tint strength is |pct| against the book's 80th-percentile move (floor 1%), so one
  // runaway position saturates instead of flattening every other row (same rule as the
  // HeatGrid face that opens this drawer).
  const tintScale = heatScale(rows.map((p) => p.unrealized_pnl_pct))

  return (
    <div className="card holdings-panel">
      <div className="card-header">
        <h3 className="card-title">
          <Wallet size={18} className="icon" aria-hidden="true" />
          Holdings
        </h3>
        {hasData && rows.length > 0 && (
          <span className="caption holdings-panel-header-note">
            <span className="num">{rows.length}</span> holding{rows.length === 1 ? '' : 's'} · <span className="num">{fmtUSD(totalValue)}</span>
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError || !hasData ? (
        <div className="empty-state">
          <div className="empty-icon"><Wallet size={28} aria-hidden="true" /></div>
          <div className="empty-text">holdings unavailable</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><Wallet size={28} aria-hidden="true" /></div>
          <div className="empty-text">no open positions</div>
        </div>
      ) : (
        <div className="holdings-panel-scroll">
          <table className="holdings-panel-table">
            <thead>
              <tr>
                <th scope="col">Symbol</th>
                <th scope="col">Qty</th>
                <th scope="col">Market value</th>
                <th scope="col">Unrealized P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((p) => {
                const cls = p.unrealized_pnl >= 0 ? 'delta-up' : 'delta-down'
                const pctValue = Number.isFinite(p.unrealized_pnl_pct) ? p.unrealized_pnl_pct : 0
                const tint = pctValue === 0 ? '' : ` holdings-panel-pnl--${pctValue > 0 ? 'gain' : 'loss'}`
                const heat = Math.min(1, Math.abs(pctValue) / tintScale) * 100
                return (
                  <tr className="holdings-panel-row" key={p.symbol}>
                    <td className="mono holdings-panel-symbol holdings-panel-cell-symbol">{p.symbol}</td>
                    <td className="num holdings-panel-cell-qty" data-label="Qty">{p.quantity}</td>
                    <td className="num holdings-panel-cell-value">{fmtUSD(p.market_value)}</td>
                    <td
                      className={`num ${cls} holdings-panel-pnl holdings-panel-cell-pnl${tint}`}
                      data-label="Unrealized P&L"
                      style={{ ['--heat' as string]: `${Number(heat.toFixed(2))}%` }}
                    >
                      <span>{fmtSignedUSD(p.unrealized_pnl)}</span>
                      <span className="holdings-panel-pnl-pct">{fmtSignedPct(p.unrealized_pnl_pct)}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
